
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Settings, Wand2 } from "lucide-react";
import { AutomationRule } from './types/automationTypes';
import { AutomationRuleForm } from './AutomationRuleForm';
import { AutomationRulesList } from './AutomationRulesList';
import { AutomationWizard } from './components/AutomationWizard';
import { useAutomationRuleForm, AutomationRuleFormData } from './hooks/useAutomationRuleForm';
import { AutomationErrorBoundary } from './AutomationErrorBoundary';
import { LoadingState } from '@/components/error-handling/LoadingState';

interface AutomationRuleEditorProps {
  rules: AutomationRule[];
  onCreateRule: (rule: AutomationRuleFormData) => void;
  onUpdateRule: (id: string, updates: Partial<AutomationRule>) => void;
  onDeleteRule: (id: string) => void;
  isLoading: boolean;
}

export const AutomationRuleEditor: React.FC<AutomationRuleEditorProps> = ({
  rules,
  onCreateRule,
  onUpdateRule,
  onDeleteRule,
  isLoading,
}) => {
  const [editingRule, setEditingRule] = React.useState<AutomationRule | null>(null);
  const [isWizardOpen, setIsWizardOpen] = React.useState(false);

  const createForm = useAutomationRuleForm(
    undefined,
    (data: AutomationRuleFormData) => {
      onCreateRule(data);
    }
  );

  const editForm = useAutomationRuleForm(
    editingRule || undefined,
    (data: AutomationRuleFormData) => {
      if (editingRule) {
        onUpdateRule(editingRule.id, data);
        setEditingRule(null);
      }
    }
  );

  const handleEdit = (rule: AutomationRule) => {
    setEditingRule(rule);
    editForm.form.reset({
      name: rule.name,
      description: rule.description,
      enabled: rule.enabled,
      trigger: rule.trigger,
      actions: rule.actions,
      priority: rule.priority,
    });
  };

  const handleToggle = (id: string, enabled: boolean) => {
    onUpdateRule(id, { enabled });
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
    editForm.form.reset();
  };

  const handleWizardComplete = (rule: AutomationRuleFormData) => {
    onCreateRule(rule);
    setIsWizardOpen(false);
  };

  return (
    <AutomationErrorBoundary>
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              Automation Rules
            </CardTitle>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsWizardOpen(true)} 
                className="gap-2"
                disabled={isLoading}
              >
                <Wand2 className="h-4 w-4" />
                Use Wizard
              </Button>
              <Button onClick={createForm.openDialog} className="gap-2" disabled={isLoading}>
                <Plus className="h-4 w-4" />
                Create Rule
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <LoadingState message="Loading automation rules..." />
            ) : (
              <AutomationErrorBoundary>
                <AutomationRulesList
                  rules={rules}
                  onEdit={handleEdit}
                  onDelete={onDeleteRule}
                  onToggle={handleToggle}
                  isLoading={isLoading}
                />
              </AutomationErrorBoundary>
            )}
          </CardContent>
        </Card>

        {/* Automation Wizard */}
        <AutomationWizard
          isOpen={isWizardOpen}
          onClose={() => setIsWizardOpen(false)}
          onComplete={handleWizardComplete}
        />

        {/* Create Rule Dialog */}
        <Dialog open={createForm.isDialogOpen} onOpenChange={createForm.closeDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Automation Rule</DialogTitle>
            </DialogHeader>
            <AutomationErrorBoundary>
              <AutomationRuleForm
                form={createForm.form}
                onSubmit={createForm.handleSubmit}
                onCancel={createForm.closeDialog}
                isLoading={isLoading}
                validationResult={createForm.validationResult}
                isValidating={createForm.isValidating}
              />
            </AutomationErrorBoundary>
          </DialogContent>
        </Dialog>

        {/* Edit Rule Dialog */}
        <Dialog open={!!editingRule} onOpenChange={handleCancelEdit}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Automation Rule</DialogTitle>
            </DialogHeader>
            <AutomationErrorBoundary>
              <AutomationRuleForm
                form={editForm.form}
                onSubmit={editForm.handleSubmit}
                onCancel={handleCancelEdit}
                isLoading={isLoading}
                validationResult={editForm.validationResult}
                isValidating={editForm.isValidating}
              />
            </AutomationErrorBoundary>
          </DialogContent>
        </Dialog>
      </div>
    </AutomationErrorBoundary>
  );
};
